import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Trash2, Plus, Mail, Send, BellRing } from "lucide-react";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { api } from "@/lib/api";

export default function WatchlistPanel() {
  const [email, setEmail] = useState(
    () => localStorage.getItem("wl_email") || ""
  );
  const [tickers, setTickers] = useState("");
  const [threshold, setThreshold] = useState(-10);

  const qc = useQueryClient();

  const listQ = useQuery({
    queryKey: ["watchlists", email],
    queryFn: async () => {
      if (!email) return [];
      const { data } = await api.get(`/watchlists`, { params: { email } });
      return data;
    },
    enabled: !!email,
  });

  const createMut = useMutation({
    mutationFn: async () =>
      (
        await api.post(`/watchlists`, {
          email,
          tickers: tickers
            .split(/[,\s]+/)
            .map((t) => t.trim())
            .filter(Boolean),
          threshold_pct: threshold,
        })
      ).data,
    onSuccess: () => {
      localStorage.setItem("wl_email", email);
      setTickers("");
      qc.invalidateQueries({ queryKey: ["watchlists", email] });
      toast.success("Watchlist created");
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "Failed to create watchlist"),
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/watchlists/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["watchlists", email] });
      toast.success("Watchlist removed");
    },
  });

  const testMut = useMutation({
    mutationFn: async (id) =>
      (await api.post(`/watchlists/${id}/test`)).data,
    onSuccess: (data) => {
      if (data?.sent) toast.success("Test alert email sent");
      else toast.error(data?.reason || "Alert queued (email not sent)");
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "Test alert failed"),
  });

  return (
    <Card
      data-testid="watchlist-panel"
      className="bg-[#121214] border-white/10 rounded-md p-5"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold tracking-tight">Watchlist alerts</h3>
        <BellRing className="h-4 w-4 text-zinc-500" />
      </div>
      <p className="mt-1 text-xs font-mono text-zinc-500">
        Get an email when a watched ticker breaches your threshold on the daily scan.
      </p>

      <div className="mt-4 space-y-3">
        <div>
          <label className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">
            Email
          </label>
          <Input
            data-testid="watchlist-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="mt-1 bg-[#09090B] border-white/10 font-mono text-sm"
          />
        </div>

        <div>
          <label className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">
            Tickers (comma separated)
          </label>
          <Input
            data-testid="watchlist-tickers-input"
            value={tickers}
            onChange={(e) => setTickers(e.target.value)}
            placeholder="BHP, CBA, WOW"
            className="mt-1 bg-[#09090B] border-white/10 font-mono text-sm uppercase"
          />
        </div>

        <div>
          <label className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">
            <span>Alert threshold</span>
            <span className="text-[#FF6B62] font-mono normal-case tracking-normal">
              {threshold.toFixed(0)}% or worse
            </span>
          </label>
          <Slider
            data-testid="watchlist-threshold-slider"
            min={-30}
            max={-3}
            step={1}
            value={[threshold]}
            onValueChange={([v]) => setThreshold(v)}
            className="mt-3"
          />
        </div>

        <Button
          data-testid="watchlist-create-button"
          disabled={createMut.isPending || !email || !tickers}
          onClick={() => createMut.mutate()}
          className="w-full bg-[#FF3B30] hover:bg-[#e0342b] text-white font-mono text-xs uppercase tracking-widest rounded-md"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add watchlist
        </Button>
      </div>

      {email && listQ.data?.length > 0 && (
        <>
          <Separator className="my-5 bg-white/10" />
          <div className="space-y-2" data-testid="watchlist-list">
            {listQ.data.map((wl) => (
              <div
                key={wl.id}
                data-testid={`watchlist-item-${wl.id}`}
                className="rounded-md border border-white/10 bg-[#09090B] p-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap gap-1.5">
                      {wl.tickers.map((t) => (
                        <Badge
                          key={t}
                          variant="outline"
                          className="rounded-md border-white/15 bg-white/5 text-zinc-200 font-mono text-[10px] px-2 py-0.5"
                        >
                          {t}
                        </Badge>
                      ))}
                    </div>
                    <div className="mt-2 flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                      <span className="text-[#FF6B62]">
                        {wl.threshold_pct.toFixed(0)}% or worse
                      </span>
                      {wl.last_alerted_at && (
                        <span>· Last alert {new Date(wl.last_alerted_at).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      data-testid={`watchlist-test-${wl.id}`}
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-zinc-400 hover:text-white"
                      onClick={() => testMut.mutate(wl.id)}
                      disabled={testMut.isPending}
                      title="Send test alert"
                    >
                      <Send className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      data-testid={`watchlist-delete-${wl.id}`}
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-zinc-400 hover:text-[#FF6B62]"
                      onClick={() => deleteMut.mutate(wl.id)}
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {email && listQ.data && listQ.data.length === 0 && (
        <p className="mt-4 text-xs font-mono text-zinc-500">
          No watchlists yet for {email}.
        </p>
      )}
    </Card>
  );
}
